import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  ShoppingCart, 
  Plus,
  Minus,
  Trash2,
  ChevronLeft,
  Truck,
  MapPin,
  CreditCard
} from 'lucide-react';

const PharmacyCartPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = i18n.language;

  const [cartItems, setCartItems] = useState(location.state?.cartItems || []);
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);

  const updateQuantity = (id, delta) => {
    setCartItems(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeItem = (id) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
    toast.success(lang === 'ar' ? 'تم إزالة المنتج' : 'Item removed');
  };

  const applyCoupon = () => {
    if (couponCode.toLowerCase() === 'pharmacy10') {
      setDiscount(10);
      toast.success(lang === 'ar' ? 'تم تطبيق الكوبون' : 'Coupon applied!');
    } else {
      toast.error(lang === 'ar' ? 'كوبون غير صحيح' : 'Invalid coupon');
    }
  };

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const deliveryFee = subtotal > 50 ? 0 : 5;
  const discountAmount = (subtotal * discount) / 100;
  const total = subtotal - discountAmount + deliveryFee;

  const handleCheckout = () => {
    navigate('/pharmacy/checkout', { state: { cartItems, subtotal, discount: discountAmount, deliveryFee, total } });
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="empty-cart">
        <div className="container mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ChevronLeft className="h-4 w-4 mr-2" />
            {lang === 'ar' ? 'رجوع' : 'Back'}
          </Button>
          <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
            <CardContent className="p-12 text-center">
              <ShoppingCart className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                {lang === 'ar' ? 'سلة التسوق فارغة' : 'Your cart is empty'}
              </h2>
              <p className="text-slate-500 mb-6">
                {lang === 'ar' ? 'أضف أدوية للبدء' : 'Add medicines to get started'}
              </p>
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => navigate('/pharmacy/medicines')}>
                {lang === 'ar' ? 'تصفح الأدوية' : 'Browse Medicines'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="pharmacy-cart">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                {lang === 'ar' ? 'سلة التسوق' : 'Shopping Cart'}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {cartItems.length} {lang === 'ar' ? 'منتجات' : 'items'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map(item => (
              <Card key={item.id} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium text-slate-900 dark:text-white">{item.name}</h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{item.description}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => removeItem(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <span className="font-bold text-green-600">${item.price}</span>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.id, -1)}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.id, 1)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="dark:bg-slate-800 border-0 shadow-sm sticky top-6">
              <CardHeader>
                <CardTitle className="text-lg">
                  {lang === 'ar' ? 'ملخص الطلب' : 'Order Summary'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Coupon */}
                <div className="flex gap-2">
                  <Input
                    placeholder={lang === 'ar' ? 'كود الخصم' : 'Coupon code'}
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                  <Button variant="outline" onClick={applyCoupon}>
                    {lang === 'ar' ? 'تطبيق' : 'Apply'}
                  </Button>
                </div>

                <Separator />

                {/* Price Breakdown */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{lang === 'ar' ? 'الخصم' : 'Discount'} ({discount}%)</span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'رسوم التوصيل' : 'Delivery'}</span>
                    <span>{deliveryFee === 0 ? (lang === 'ar' ? 'مجاني' : 'Free') : `$${deliveryFee}`}</span>
                  </div>
                </div>

                <Separator />

                <div className="flex justify-between font-bold text-lg">
                  <span>{lang === 'ar' ? 'الإجمالي' : 'Total'}</span>
                  <span className="text-green-600">${total.toFixed(2)}</span>
                </div>

                {subtotal < 50 && (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                    <Truck className="h-4 w-4 inline mr-2" />
                    {lang === 'ar' 
                      ? `أضف $${(50 - subtotal).toFixed(2)} للحصول على توصيل مجاني`
                      : `Add $${(50 - subtotal).toFixed(2)} for free delivery`
                    }
                  </div>
                )}
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700" 
                  size="lg"
                  onClick={handleCheckout}
                >
                  {lang === 'ar' ? 'إتمام الطلب' : 'Proceed to Checkout'}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PharmacyCartPage;
